import {
    Heading,
    StatusBadge,
    Badge,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    PageHeader,
    PageHeaderBreadcrumb,
    PageHeaderRow,
    PageHeaderIdentity,
    PageHeaderInfo,
    PageHeaderTitleRow,
    PageHeaderMeta,
    PageHeaderMetaItem,
    PageHeaderMetaDot,
    PageHeaderActions,
    Container,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Avatar,
    AvatarFallback,
    AvatarImage,
    Button,
    OrgAvatar,
    OrganizationDetailSkeleton,
} from "@athena/ui";
import { Users, SquareMenu, SquareStack, Building, UserPlus, Plus } from "lucide-react";
import { useState } from "react";
import { OrganizationUsers } from "./OrganizationUsers";
import { OrganizationResource } from "./OrganizationResource";
import { OrganizationSubOrg } from "./OrganizationSubOrg";
import { OrganizationDetail } from "./OrganizationDetail";
import { useQuery } from "@apollo/client/react";
import { useParams, useSearchParams } from "react-router-dom";
import { OrganizationByIdDocument, OrganizationRosterCountDocument } from "../../graphql/organizations.generated";
import { useAutoEnrollRun } from "@/hooks/useAutoEnrollRun";
import { ImageUrlDocument } from "@/graphql/media.generated";
import { formatDateTime } from "../../../../global/formatHelpers";
import { OrganizationAutoEnroll } from "./OrganizationAutoEnroll";

const ADD_BUTTON_BY_TAB: Record<string, string> = {
    users: "Add New Members",
    "sub-orgs": "Add New Sub-org",
    resources: "Add New Resource",
};

export function OrganizationPage() {
    const { id } = useParams<{ id: string }>();
    const [
        searchParams,
        setSearchParams,
    ] = useSearchParams();
    const activeTab = searchParams.get("tab") ?? "overview";
    const [
        addUsersOpen,
        setAddUsersOpen,
    ] = useState<boolean>(false);
    const [
        newSubOrgOpen,
        setNewSubOrgOpen,
    ] = useState<boolean>(false);
    const [
        addResourceOpen,
        setAddResourceOpen,
    ] = useState<boolean>(false);
    const addButtonLabel = ADD_BUTTON_BY_TAB[activeTab];
    const { data, loading, refetch } = useQuery(OrganizationByIdDocument, {
        variables: { id: id ?? "" },
        skip: !id,
    });

    const org = data?.organization;
    // A sub-org's eligible members are restricted to its parent org's members — undefined (no
    // restriction) for a top-level org, which has no parent. Passed as the parent's *id* rather
    // than its member ids: the picker filters on the server, so the list of eligible people never
    // has to come down the wire to be intersected here.
    const restrictToOrganizationId = org?.parentOrganization?.id;
    /*
     * The invited count, counted in the database.
     *
     * This page used to also fetch the invitation rows themselves, un-paged, and hand them to the
     * Users tab — a capped read of up to 1,000 rows on every tab of this page, whose `.length`
     * then reported 1,000 invited for every organization past the cap. The count is what the
     * header needs; the Users tab pages the rows it shows for itself.
     */
    const { data: pendingCountData } = useQuery(OrganizationRosterCountDocument, {
        variables: { organizationId: id ?? "", status: "PENDING" },
        skip: !id,
    });
    const pendingCount = pendingCountData?.organizationRosterCount ?? 0;

    const { data: logoUrlData } = useQuery(ImageUrlDocument, {
        variables: { key: org?.logoUrl ?? "" },
        skip: !org?.logoUrl,
    });

    /*
     * Above the guards below, deliberately: this calls a hook, so returning the skeleton first
     * would skip it on the loading render and call it on the next one, which React sees as a
     * changed hook order.
     *
     * Read here rather than inside the auto-enroll tab because the badge has to be visible from
     * the other tabs — the whole point is knowing an upload is still going while looking at
     * something else.
     */
    const autoEnrollRun = useAutoEnrollRun(id ?? "");

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, and
    // swapping the page for a skeleton unmounted the auto-enroll panel mid-run —
    // losing the results table it had just filled in, and aborting its in-flight
    // roster read, which surfaced as "The operation was aborted."
    if (loading && !data) return <OrganizationDetailSkeleton />;

    if (!org) return null;

    // The API combines memberships and pending auto-enrollment invitations for every surface that
    // presents a user-facing roster total. Keep `memberCount` for membership-only behavior below.
    const userCount = org.userCount;

    // The query fetches 3 parentOrganization levels up — plenty for any realistic
    // depth — so this is spelled out explicitly rather than as a loop, since each
    // level's generated type only knows about the level directly beneath it.
    const parent = org.parentOrganization;
    const grandparent = parent?.parentOrganization;
    const greatGrandparent = grandparent?.parentOrganization;
    const ancestors: { id: string; name: string }[] = [
        ...(greatGrandparent ? [greatGrandparent] : []),
        ...(grandparent ? [grandparent] : []),
        ...(parent ? [parent] : []),
    ];

    return (
        <Tabs value={activeTab} onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}>
            <PageHeader className="pb-0">
                <PageHeaderBreadcrumb>
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/organizations">Organizations</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            {ancestors.flatMap((ancestor) => [
                                <BreadcrumbItem key={`${ancestor.id}-item`}>
                                    <BreadcrumbLink href={`/organizations/${ancestor.id}`}>
                                        {ancestor.name}
                                    </BreadcrumbLink>
                                </BreadcrumbItem>,
                                <BreadcrumbSeparator key={`${ancestor.id}-sep`} />,
                            ])}
                            <BreadcrumbItem>
                                <BreadcrumbPage>{org.name}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </PageHeaderBreadcrumb>

                <PageHeaderRow>
                    <PageHeaderIdentity>
                        {logoUrlData?.imageUrl ? (
                            <Avatar>
                                <AvatarImage src={logoUrlData?.imageUrl ?? undefined} alt={org.name} />
                                <AvatarFallback>{org.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                        ) : (
                            <OrgAvatar id={org.id} fallback={org.name.slice(0, 2).toUpperCase()} size="lg" />
                        )}
                        <PageHeaderInfo>
                            <PageHeaderTitleRow>
                                <Heading level={3}>{org.name}</Heading>
                            </PageHeaderTitleRow>
                            <PageHeaderMeta>
                                <PageHeaderMetaItem>added {formatDateTime(org.createdAt)}</PageHeaderMetaItem>
                                <PageHeaderMetaDot />
                                <PageHeaderMetaItem>{userCount.toLocaleString()} users</PageHeaderMetaItem>
                                <PageHeaderMetaDot />
                                {/*
                                 * Read against the count beside it: "412 of 780 on the app" is the
                                 * adoption question somebody opens this page asking. Suppressed for
                                 * an empty org, where the share would be a division by zero.
                                 */}
                                {userCount > 0 && (
                                    <>
                                        <PageHeaderMetaItem>
                                            {org.appUserCount.toLocaleString()} on the app (
                                            {Math.round((org.appUserCount / userCount) * 100)}%)
                                        </PageHeaderMetaItem>
                                        <PageHeaderMetaDot />
                                    </>
                                )}
                                {pendingCount > 0 && (
                                    <>
                                        <PageHeaderMetaItem>{pendingCount.toLocaleString()} invited</PageHeaderMetaItem>
                                        <PageHeaderMetaDot />
                                    </>
                                )}
                                <PageHeaderMetaItem>{org.subOrganizations.length} sub-organizations</PageHeaderMetaItem>
                                <PageHeaderMetaDot />
                                {org.chatEnabled && (
                                    <PageHeaderMetaItem>
                                        <StatusBadge dot variant="success">
                                            Chat enabled
                                        </StatusBadge>
                                    </PageHeaderMetaItem>
                                )}
                                {autoEnrollRun?.running && (
                                    <PageHeaderMetaItem>
                                        {/*
                                         * The count rides along because the useful question during
                                         * a ten-minute upload is not whether it is running but how
                                         * far it has got.
                                         */}
                                        <StatusBadge dot variant="warning">
                                            Auto-enroll in progress — {autoEnrollRun.settled.toLocaleString()} of{" "}
                                            {autoEnrollRun.total.toLocaleString()}
                                        </StatusBadge>
                                    </PageHeaderMetaItem>
                                )}
                            </PageHeaderMeta>
                        </PageHeaderInfo>
                    </PageHeaderIdentity>
                    {addButtonLabel && (
                        <PageHeaderActions>
                            <Button
                                size="sm"
                                className="flex items-center gap-1.5"
                                onClick={() => {
                                    if (activeTab === "users") setAddUsersOpen(true);
                                    else if (activeTab === "sub-orgs") setNewSubOrgOpen(true);
                                    else if (activeTab === "resources") setAddResourceOpen(true);
                                }}
                            >
                                <Plus className="h-3.5 w-3.5" />
                                {addButtonLabel}
                            </Button>
                        </PageHeaderActions>
                    )}
                </PageHeaderRow>
                <PageHeaderRow>
                    <TabsList variant="line" className="bg-card">
                        <TabsTrigger variant="line" value="overview" className="data-[state=active]:border-secondary">
                            <Building className="text-muted-foreground mr-2 h-4 w-4" /> Organization Details
                        </TabsTrigger>
                        <TabsTrigger variant="line" value="users" className="data-[state=active]:border-secondary">
                            <Users className="text-muted-foreground mr-2 h-4 w-4" /> Users{" "}
                            <Badge className="bg-muted hover:bg-muted pointer-events-none mx-2 text-neutral-400">
                                {userCount.toLocaleString()}
                            </Badge>
                        </TabsTrigger>
                        <TabsTrigger variant="line" value="resources" className="data-[state=active]:border-secondary">
                            <SquareMenu className="text-muted-foreground mr-2 h-4 w-4" />
                            Resources
                        </TabsTrigger>
                        <TabsTrigger
                            variant="line"
                            value="auto-enroll"
                            className="data-[state=active]:border-secondary"
                        >
                            <UserPlus className="text-muted-foreground mr-2 h-4 w-4" />
                            Auto-Enrollment
                        </TabsTrigger>
                        <TabsTrigger variant="line" value="sub-orgs" className="data-[state=active]:border-secondary">
                            <SquareStack className="text-muted-foreground mr-2 h-4 w-4" /> Sub-orgs{" "}
                            <Badge className="bg-muted hover:bg-muted pointer-events-none mx-2 text-neutral-400">
                                {org.subOrganizations.length}
                            </Badge>
                        </TabsTrigger>
                    </TabsList>
                </PageHeaderRow>
            </PageHeader>
            <Container maxWidth="full" className="p-6">
                <TabsContent value="overview">
                    <OrganizationDetail org={org} />
                </TabsContent>
                <TabsContent value="users">
                    <OrganizationUsers
                        organizationId={org.id}
                        memberCount={org.memberCount}
                        onMembersAdded={() => void refetch()}
                        restrictToOrganizationId={restrictToOrganizationId}
                        pendingCount={pendingCount}
                        addMemberOpen={addUsersOpen}
                        onAddMemberOpenChange={setAddUsersOpen}
                    />
                </TabsContent>
                <TabsContent value="sub-orgs">
                    <OrganizationSubOrg
                        subOrganizations={org.subOrganizations}
                        parentOrganizationId={org.id}
                        onSubOrgCreated={() => void refetch()}
                        onSubOrgMembersAdded={() => void refetch()}
                        onSubOrgDeleted={() => void refetch()}
                        onSubOrgRenamed={() => void refetch()}
                        newSubOrgOpen={newSubOrgOpen}
                        onNewSubOrgOpenChange={setNewSubOrgOpen}
                    />
                </TabsContent>
                <TabsContent value="auto-enroll">
                    <OrganizationAutoEnroll org={org} />
                </TabsContent>
                <TabsContent value="resources">
                    <OrganizationResource
                        orgId={org.id}
                        createOpen={addResourceOpen}
                        onCreateOpenChange={setAddResourceOpen}
                    />
                </TabsContent>
            </Container>
        </Tabs>
    );
}
